'use client';

import { useEffect, useRef } from 'react';

import { debounce } from '@bridge/utils';

/**
 * Returns a stable debounced wrapper around `callback`. The wrapper identity
 * persists across renders, so the internal timer isn't reset every time the
 * parent re-renders. When `callback` changes, an internal ref is updated so
 * pending invocations call the latest version — again without resetting the
 * timer.
 *
 * Use this (not `useDebounce`) when you want to debounce a *callback* rather
 * than a *value*. `useDebounce` is value-based and isn't a drop-in for
 * bidirectional-sync scenarios (e.g. URL-backed inputs) where external
 * changes shouldn't fire outbound writes.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number,
): (...args: TArgs) => void {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedRef = useRef<((...args: TArgs) => void) | null>(null);
  if (!debouncedRef.current) {
    debouncedRef.current = debounce((...args: TArgs) => callbackRef.current(...args), delay);
  }
  return debouncedRef.current;
}
